import moment from "moment";
import store from "../store";

/**
 * get room object by key
 * @param {String} eventKey
 * @returns {Object} room
 */
export function getRoom(eventKey) {
  const allRooms = store.getters.allRooms;
  if (!allRooms) return null;
  if (allRooms[eventKey]) return { key: eventKey, value: allRooms[eventKey] };
  return null;
}

function getSurgOrders(orders) {
  if (Array.isArray(orders)) return orders;
  if (typeof orders === "object") return Object.values(orders);
  return [];
}

/**
 * build event type appointment
 * @param {Object} event
 * @param {String} routeName
 * @returns {Object} formatted event
 */
export function buildAppointmentEvent(event, routeName) {
  // build string template for mobile - home component
  const startTimeFormat = moment(new Date(event.start)).format("HH:mm");
  let appContent = "<b>" + startTimeFormat + "</b> ";
  if (routeName == "surg-app-list") {
    appContent += "<b>ייעוץ |</b> ";
  }
  appContent +=
    event.patientName +
    " " +
    event.passport +
    " | " +
    event.description +
    " | " +
    event.patient_phone;
  if (event.notes) appContent += " | " + event.notes;
  if (routeName == "calendar") {
    appContent += " רופא: " + event.doc_name;
  }
  if (event.tafnit_receipt_no) {
    appContent += " | סטטוס תשלום: שולם";
  }
  const appScheduledBy = store.getters["getAppScheduledBy"](
    event.scheduled_by,
    false
  );
  if (appScheduledBy) {
    appContent += `<img class="cal-day-info-left-ic" src="${appScheduledBy.image}" alt="${appScheduledBy.text}" />`;
  }
  const bubbleStatus = store.getters["getAppBubbleStatus"](
    event.status,
    event.arrival_approval
  );

  if (bubbleStatus) {
    appContent += `<span class="flex flex--content--center cal-day-info-left-ok" style="${bubbleStatus.color}">${bubbleStatus.text}</span>`;
  }
  /**
   * create fake start & end for event -
   * it's necessary if event duration is less than 15 minuets
   * for that the client can see all the details of the event inside vue cal
   */
  event.fakeStart = event.start;
  event.fakeEnd = event.end;
  const appDuration = Math.abs(
    moment(event.start).diff(moment(event.end), "minutes")
  );
  if (appDuration < 15) {
    event.fakeEnd = moment(event.start).add("10", "minutes");
  }
  //  build new object - all the keys are required
  return {
    start: new Date(event.fakeStart),
    end: new Date(event.fakeEnd),
    real_start: new Date(event.start),
    real_end: new Date(event.end),
    app_duration: appDuration,
    id: event.appointment_id,
    title: event.title,
    content: appContent,
    class: "appointment " + (appDuration < 15 ? "short_app" : ""),
    background: false,
    type: "appointment",
    time: startTimeFormat,
    patientName: event.patientName,
    patientBirthDate: event.birth_date,
    patientPassport: event.passport,
    appointmentName: event.appointment_name,
    patientPhone: event.patient_phone,
    notes: event.notes,
    parent_block_id: event.parent_block_id,
    room: getRoom(event.resourceId),
    arrival_approval: event.arrival_approval, // backend issue this is approval status
    status: event.status, // backend issue this is arrival status
    scheduled_by: event.scheduled_by,
    description: event.description,
    insurance_name: event.insurance_name,
    patient_age: event.patient_age,
    payment_status: event.tafnit_receipt_no,
    insurance_fee: event.insurance_fee
  };
}

/**
 * build event type block
 * @param {Object} event
 * @returns {Object} formatted event
 */
export function buildBlockEvent(event) {
  // check if the block is private - "חסוי" / or if the block pending
  const isPrivate = event.block_status == "private";
  const isPending = event.block_status == "pending";
  // build string template for mobile - home component
  let privateBlockString = "";
  if (isPrivate) {
    privateBlockString = `<div
                class="is_private-tx bold flex flex--start"
              >
                <img src="/assets/img/svg/blocked.svg" />
                חסוי
              </div>`;
  }
  // get block room
  const room = getRoom(event.resourceId);
  const startTime = moment(event.start).format("HH:mm");
  const endTime = moment(event.end).format("HH:mm");
  let block_title = `<span class="bold">${endTime} - ${startTime}</span> ${event.block_type_name} |`;
  if (room && room.value) block_title += " " + room.value.name;
  block_title += " " + privateBlockString;
  //  get block class
  let blockClass = "Block ";
  if (isPrivate) blockClass += " Block--private";
  if (isPending) blockClass += " Block--pending";
  //  build new object - all the keys are required

  let newEnd = event.end;
  if (
    event.end.includes("T00:00:00") &&
    window.location.pathname == "/dashboard/surgeries" &&
    window.innerWidth > 1200
  ) {
    // new end for blocks that ended in 00:00:00, only for desktop bug
    newEnd = moment(event.end, "YYYY-MM-DDTHH:mm:ss")
      .subtract(1, "seconds")
      .format("YYYY-MM-DDTHH:mm:ss");
  }

  return {
    start: new Date(event.start),
    end: new Date(newEnd),
    id: event.background_block_id,
    title: event.block_type_name,
    content: block_title,
    class: blockClass,
    is_private: isPrivate,
    is_pending: isPending,
    background: true,
    bg_menu: false,
    type: "block",
    room
  };
}

/**
 * build event type surgery
 * @param {Object} event
 * @returns {Object} formatted event
 */
export function buildSurgeryEvent(event) {
  //  build new object - all the keys are required
  let tmp = {
    start: new Date(event.start),
    end: new Date(event.end),
    id: event.id,
    title: event.title,
    content: "",
    class: "surgery",
    background: false,
    type: "surgery",
    room: getRoom(event.resourceId),
    patient_name: event.patientName,
    patient_age: event.patient_age,
    passport: event.passport,
    phone: event.patient_phone,
    insurance_status_heb: event.insurance_status_heb,
    insurance_code: event.insurance_code,
    insurance_name: event.insurance_name,
    status: event.status_heb,
    surgeryName: event.surgeryName,
    insurance_fee: event.insurance_fee,
    body_side: event.body_side,
    notes: event.notes,
    bg_menu: false,
    dotsMenu: "",
    anesthesia: event.anesthesia,
    gender: event.patient_gender,
    assistantStatus: event.assistant_status,
    hospitalizationDays: event.hospitalization,
    orders: getSurgOrders(event.orders),
    surgery_resources: event.surgery_resources,
    tafnit_receipt_no: event.tafnit_receipt_no
  };
  // build string template for mobile - home component
  const startTimeFormat = moment(new Date(event.start)).format("HH:mm");
  let surgName = tmp.surgeryName;
  if (tmp.orders && tmp.orders.length) {
    surgName = "";
    tmp.orders.forEach((o, i) => {
      const isComma = i !== tmp.orders.length - 1;
      surgName += `<div style="display: inline-block;">${o.tafnit_code} > ${
        o.tafnit_name
      } ${isComma ? "," : ""}</div>`;
    });
  }

  if (tmp.status) {
    let statusClass = store.getters["getSurgStatusClass"](tmp.status);
    tmp.content += `<div class="label-status push-right ${statusClass}">${tmp.status}</div>`;
  }
  tmp.content += `<b>${startTimeFormat} - ${tmp.patient_name}</b>
          <div class="event-title-inner">${tmp.passport} | ${
    tmp.phone ? tmp.phone + " | " : ""
  } <span class="small-text">${surgName}</span>`;
  if (tmp.insurance_fee) {
    tmp.content += ` | סך השתתפות עצמית: ₪${tmp.insurance_fee}`;
  }
  if (tmp.insurance_status_heb) {
    tmp.content += ` | סטטוס התחייבות: ${tmp.insurance_status_heb}`;
  }
  if (tmp.insurance_code) {
    tmp.content += `| מספר התחייבות:  ${tmp.insurance_code}`;
  }
  if (tmp.patient_age) {
    tmp.content += ` | גיל המטופל:  ${tmp.patient_age}`;
  }
  if (tmp.gender) {
    tmp.content += ` | מספר התחייבות:  ${tmp.gender}`;
  }
  // if (this.isBeforeToday(tmp.start)) {
  tmp.content += "</div>";
  return tmp;
}

/**
 * get which type of events needed from api - fullcalendar_events_ajax
 * @param {String} routeName
 * @returns {String} type
 */
export function getEventsType(routeName) {
  let event_type = "appointment";
  if (routeName == "surgeries" || routeName == "surgeries-list") {
    event_type = "surgery_gastro_ivf_catheterization";
  } else if (routeName == "calendar" || routeName == "surg-app-list") {
    event_type = "appointment_surgery_gastro_ivf_catheterization";
  }
  return event_type;
}
